import { createInstantSearchRouterNext } from 'react-instantsearch-router-nextjs';
import singletonRouter from 'next/router';
import { format, fromUnixTime, getUnixTime, parse } from 'date-fns';

import { flatSortIndexForSlug, flatSortSlugForIndex } from './flats-sort';

import type { TypeFlat } from '@/types/analytics/flat';
import type { InstantSearchProps } from 'react-instantsearch';

/**
 * Routing de dos vías del listado de flats: mapea el uiState de InstantSearch
 * a slugs de query "marketing-friendly" en la URL (y de vuelta), de modo que
 * cualquier combinación de filtros sea enlazable/compartible y el back/forward
 * del navegador funcione.
 *
 * La ZONA NO va en el query: vive en el path (`/venta-pisos/madrid`) y la
 * siembra `getServerSideProps` vía `initialUiState`. `routeToState` la
 * re-inyecta SIEMPRE porque `createRouterMiddleware` fusiona el initialUiState
 * con `routeToState(...)` mediante un spread *shallow* por índice — si no la
 * devolviéramos aquí, el hierarchicalMenu de la zona se perdería tras hidratar.
 */

type FlatType = TypeFlat['ventaAlquiler'];

const SIZE_ATTR = 'metrosComputablesUA';
const CARACT_ATTR = 'caracteristicasFacet.description';
const ZONE_ATTR = 'hierarchicalZonas.lvl0';
const DATE_FMT = 'dd-MM-yyyy';

const priceAttribute = (type: FlatType) =>
  type === 'VENTA' ? 'importeVenta' : 'importeRecibofinal';

/** Slugs de query soportados — reutilizado por el guard de 404 en las páginas. */
export const FLAT_FILTER_SLUGS = [
  'tipo',
  'banos',
  'estado',
  'dormitorios',
  'caracteristicas',
  'precio-min',
  'precio-max',
  'metros',
  'desde',
  'hasta',
  'q',
  'pagina',
  'orden',
] as const;

type MappingParams = {
  indexName: string;
  type: FlatType;
  /**
   * Cadena jerárquica de zona (p.ej. `madrid > madrid > cañaveral`), o
   * `undefined` en el listado sin zona (`/venta-pisos/viviendas`).
   */
  zoneHierarchical?: string;
};

export const createFlatsStateMapping = ({
  indexName,
  type,
  zoneHierarchical,
}: MappingParams) => {
  const priceAttr = priceAttribute(type);

  const stateToRoute = (uiState: Record<string, any>) => {
    const idx = uiState[indexName] || {};
    const route: Record<string, string | number> = {};

    if (idx.menu?.tipoDeUsoDescription)
      route.tipo = idx.menu.tipoDeUsoDescription;
    if (idx.menu?.banos) route.banos = idx.menu.banos;
    if (idx.menu?.estadoDescripcion) route.estado = idx.menu.estadoDescripcion;
    if (idx.menu?.habitaciones) route.dormitorios = idx.menu.habitaciones;

    const caract = idx.refinementList?.[CARACT_ATTR];
    if (caract?.length) route.caracteristicas = caract.join(',');

    const price = idx.range?.[priceAttr];
    if (price) {
      const [min, max] = String(price).split(':');
      if (min) route['precio-min'] = min;
      if (max) route['precio-max'] = max;
    }
    const size = idx.range?.[SIZE_ATTR];
    if (size) {
      const [min] = String(size).split(':');
      if (min) route.metros = min;
    }

    if (idx.medEstDates?.start && idx.medEstDates?.end) {
      route.desde = format(fromUnixTime(idx.medEstDates.start), DATE_FMT);
      route.hasta = format(fromUnixTime(idx.medEstDates.end), DATE_FMT);
    }

    // `sortBy` es el nombre de la réplica; en la URL va como slug. El orden por
    // defecto no se serializa, para no ensuciar la URL con `?orden=relevancia`.
    const sortSlug = flatSortSlugForIndex(indexName, idx.sortBy);
    if (sortSlug) route.orden = sortSlug;

    if (idx.query) route.q = idx.query;
    if (idx.page && idx.page > 1) route.pagina = idx.page;

    return route;
  };

  const routeToState = (routeState: Record<string, any> = {}) => {
    const idx: Record<string, any> = {};

    if (zoneHierarchical) {
      idx.hierarchicalMenu = { [ZONE_ATTR]: [zoneHierarchical] };
    }

    const menu: Record<string, string> = {};
    if (routeState.tipo) menu.tipoDeUsoDescription = String(routeState.tipo);
    if (routeState.banos) menu.banos = String(routeState.banos);
    if (routeState.estado) menu.estadoDescripcion = String(routeState.estado);
    if (routeState.dormitorios) {
      menu.habitaciones = String(routeState.dormitorios);
    }
    if (Object.keys(menu).length) idx.menu = menu;

    if (routeState.caracteristicas) {
      idx.refinementList = {
        [CARACT_ATTR]: String(routeState.caracteristicas).split(','),
      };
    }

    const range: Record<string, string> = {};
    if (routeState['precio-min'] || routeState['precio-max']) {
      range[priceAttr] = `${routeState['precio-min'] ?? ''}:${
        routeState['precio-max'] ?? ''
      }`;
    }
    if (routeState.metros) range[SIZE_ATTR] = `${routeState.metros}:`;
    if (Object.keys(range).length) idx.range = range;

    if (routeState.desde && routeState.hasta) {
      const start = getUnixTime(
        parse(String(routeState.desde), DATE_FMT, new Date()),
      );
      const end = getUnixTime(
        parse(String(routeState.hasta), DATE_FMT, new Date()),
      );
      if (Number.isFinite(start) && Number.isFinite(end)) {
        idx.medEstDates = { start, end };
      }
    }

    if (routeState.orden) {
      idx.sortBy = flatSortIndexForSlug(indexName, String(routeState.orden));
    }

    if (routeState.q) idx.query = String(routeState.q);
    if (routeState.pagina) idx.page = Number(routeState.pagina);

    return { [indexName]: idx };
  };

  return { stateToRoute, routeToState };
};

type RoutingParams = MappingParams & { serverUrl?: string };

/**
 * `routing` completo (router Next.js + stateMapping) para `<InstantSearch>`.
 * `serverUrl` sólo lo usa el adaptador en SSR (`typeof window === 'undefined'`);
 * en cliente lee de `window.location`, así que es seguro pasarlo siempre.
 */
export const buildFlatsRouting = ({
  serverUrl,
  indexName,
  type,
  zoneHierarchical,
}: RoutingParams): NonNullable<InstantSearchProps['routing']> => ({
  router: createInstantSearchRouterNext({
    singletonRouter,
    serverUrl,
  }),
  // El stateMapping usa firmas laxas (Record) a propósito; se castea al
  // contrato StateMapping<UiState> que espera el prop `routing`.
  stateMapping: createFlatsStateMapping({
    indexName,
    type,
    zoneHierarchical,
  }) as any,
});
